import {
  ArrowUpRight,
  Brain,
  CircleAlert,
  ClipboardList,
  Crosshair,
  RouteIcon,
  ScanSearch,
  Sparkles,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useSelector } from "react-redux";
import Card from "../card";
import * as S from "../dashboard/_shared";
import {
  getTop3InsightsByPriority,
  maxJourneyMetric,
  minJourneyMetric,
  npsColor,
} from "./dataTransforms";
import "./style.scss";

export default function ExecutiveView({ ctx }) {
  const insightsState = useSelector((state) => state.dashboard.insights);
  const insights = insightsState?.data?.data || [];

  const overviewState = useSelector((state) => state.dashboard.overview);
  const overview = overviewState?.data?.data || [];

  const driversState = useSelector((state) => state.dashboard.drivers);
  const drivers = driversState?.data?.data || [];

  const journeyState = useSelector((state) => state.dashboard.journey);
  const journey = journeyState?.data?.data || [];

  const maxMetric = maxJourneyMetric(journey);
  const minMetric = minJourneyMetric(journey);

  const { C, F, etSt } = S;
  const { per, setTab, isPrint } = ctx;

  return (() => {
    const top3 = getTop3InsightsByPriority(insights);
    const tipoCfg = {
      alerta: { bg: C.redBg, bc: C.red, bl: "Alerta", Icon: CircleAlert },
      oportunidade: {
        bg: C.greenBg,
        bc: C.green,
        bl: "Oportunidade",
        Icon: Zap,
      },
      padrao: { bg: C.blueBg, bc: C.blue, bl: "Padrão", Icon: ScanSearch },
      predicao: { bg: C.purpleBg, bc: C.purple, bl: "Predição", Icon: Brain },
    };

    return (
      <div
        className="tabExecutiveView"
        style={{ display: "flex", flexDirection: "column", gap: 18 }}
      >
        {/* Foco do período — hero */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: C.p500,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Foco do período
            </span>
            <span style={{ fontSize: 11, color: C.text4 }}>
              · priorizado pela Eva com base em{" "}
              {overview[0]?.respondents?.toLocaleString()} respondentes
            </span>
          </div>
          <h2
            style={{
              margin: "0 0 4px",
              fontSize: 22,
              fontWeight: 700,
              color: C.text,
              letterSpacing: "-0.03em",
            }}
          >
            Frentes que mais impactam o NPS agora
          </h2>
          <p
            style={{ margin: 0, fontSize: 13, color: C.text3, lineHeight: 1.6 }}
          >
            Resolva as duas primeiras e o NPS deve subir entre 6 e 10 pontos no
            próximo período. A terceira frente já está evoluindo bem e pode
            virar case.
          </p>
        </div>

        {/* Top 3 cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3,1fr)",
            gap: 12,
          }}
        >
          {top3?.map((ins, i) => {
            const cfg = tipoCfg[ins.category.toLowerCase()] || tipoCfg.padrao;
            const { Icon } = cfg;
            return (
              <Card
                key={i}
                style={{
                  padding: "18px 18px 16px",
                  border: `1.5px solid ${cfg.bc}30`,
                  background: cfg.bg,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 240,
                }}
              >
                {/* Rank + tipo */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 8,
                        background: cfg.bc,
                        color: "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {i + 1}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 9px",
                        background: "rgba(255,255,255,.6)",
                        borderRadius: 20,
                        border: `1px solid ${cfg.bc}25`,
                      }}
                    >
                      <Icon size={11} color={cfg.bc} strokeWidth={2.4} />
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: cfg.bc,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                        }}
                      >
                        {cfg.bl}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Title */}
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: C.text,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.35,
                    marginBottom: 8,
                  }}
                >
                  {ins.title}
                </div>
                {/* Body */}
                <div
                  style={{
                    margin: "0 0 12px",
                    fontSize: 12,
                    color: C.text2,
                    lineHeight: 1.55,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {ins.description?.split(". ").map((sentence, idx, arr) => (
                    <div key={idx}>
                      {sentence.trim()}
                      {idx < arr.length - 1 ? "." : ""}
                    </div>
                  ))}
                </div>
                {/* Ação sugerida */}
                <div
                  style={{
                    background: "rgba(255,255,255,.7)",
                    borderRadius: 9,
                    padding: "9px 11px",
                    marginBottom: 10,
                    borderLeft: `3px solid ${cfg.bc}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: cfg.bc,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      marginBottom: 2,
                    }}
                  >
                    Próximo passo
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.text,
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {ins.action}
                  </div>
                </div>
                {/* CTAs */}
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => setTab("acoes")}
                    style={{
                      flex: 1,
                      background: cfg.bc,
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                      fontFamily: "inherit",
                    }}
                  >
                    Plano de ação <ArrowUpRight size={12} />
                  </button>
                  <button
                    onClick={() => setTab("eva")}
                    style={{
                      background: "rgba(255,255,255,.7)",
                      color: cfg.bc,
                      border: `1px solid ${cfg.bc}40`,
                      borderRadius: 8,
                      padding: "8px 10px",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      fontFamily: "inherit",
                    }}
                  >
                    <Sparkles size={11} /> Eva
                  </button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Snapshot do período */}
        <Card style={{ padding: "18px 22px" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.text4,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 10,
            }}
          >
            Snapshot do período — {per.label}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 14,
            }}
          >
            {[
              {
                l: "NPS Geral",
                v: overview[0]?.nps ? `${overview[0].nps}%` : "—",
                sub: overview[0]?.nps_delta != null
                  ? `${overview[0].nps_delta > 0 ? "+" : ""}${overview[0].nps_delta} pts vs anterior`
                  : "—",
                c: npsColor(overview[0]?.nps ?? 0),
                big: true,
              },
              {
                l: "Jornada CX",
                v: `${overview[0]?.cx ? `${overview[0].cx}%` : "—"}`,
                sub: `média das ${journey.length} etapas`,
                c: etSt("cx", overview[0]?.cx ?? 0).color,
                big: true,
              },
              {
                l: "Melhor etapa",
                v: `${maxMetric.value}%`,
                sub: maxMetric.name,
                c: etSt(maxMetric.name, maxMetric.value ?? 0).color,
                big: false,
              },
              {
                l: "Pior etapa",
                v: `${minMetric.value}%`,
                sub: minMetric.name,
                c: etSt(minMetric.name, minMetric.value ?? 0).color,
                big: false,
              },
            ].map((k, i) => (
              <div
                key={i}
                style={{
                  borderRight: i < 3 ? `1px solid ${C.border}` : "none",
                  paddingRight: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: C.text4,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  {k.l}
                </div>
                <div
                  style={{
                    fontSize: k.big ? 34 : 22,
                    fontWeight: 700,
                    color: k.c,
                    letterSpacing: "-0.04em",
                    fontFamily: F.display,
                    lineHeight: 1.05,
                  }}
                >
                  {k.v}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: C.text3,
                    marginTop: 3,
                    lineHeight: 1.3,
                  }}
                >
                  {k.sub}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Atalhos para drill-down — oculto na impressão */}
        {!isPrint && <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: C.text4,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
            }}
          >
            Aprofundar
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { id: "jornada", label: "Ver jornada completa", Icon: RouteIcon },
              { id: "drivers", label: "Drivers de impacto", Icon: Crosshair },
              {
                id: "tendencias",
                label: "Tendências históricas",
                Icon: TrendingUp,
              },
              { id: "segmentos", label: "Segmentação", Icon: Users },
              { id: "acoes", label: "Plano de ação", Icon: ClipboardList },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "9px 14px",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  color: C.text2,
                  fontFamily: "inherit",
                  transition: "all .15s",
                }}
              >
                <Icon size={13} color={C.text3} />
                {label} <ArrowUpRight size={12} color={C.text4} />
              </button>
            ))}
          </div>
        </div>}
      </div>
    );
  })();
}
